import React from 'react';
import { hexAdjustHsluv, prefixHexColor, stripHexPrefix } from 'utils/colors';

// TODO: These values are hardcoded until we have proper icon lookups in place in a follow-up PR

export const hardcodedIconMap = new Map<string, React.ReactElement>([
  [
    'Logged',
    <svg
      key="Logged"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>,
  ],
  [
    'Ready for Review',
    <svg
      key="Ready for Review"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.5407 5.67647L19.432 6.08201C16.553 2.45825 11.3788 1.39065 7.25011 3.77434C4.5146 5.35369 2.86232 8.08885 2.55215 11.0029C2.49369 11.5521 2.8915 12.0447 3.44068 12.1031C3.98987 12.1616 4.48245 11.7638 4.54091 11.2146C4.78634 8.90887 6.09163 6.75259 8.25011 5.5064C11.406 3.68436 15.3356 4.41654 17.6452 7.06151L17.3013 6.96937C16.7678 6.82642 16.2195 7.14301 16.0766 7.67647C15.9336 8.20994 16.2502 8.75828 16.7837 8.90122L19.5157 9.63327C20.0492 9.77621 20.5975 9.45963 20.7405 8.92616L21.4725 6.19411C21.6155 5.66065 21.2989 5.11231 20.7654 4.96937C20.2319 4.82642 19.6836 5.14301 19.5407 5.67647Z"
        fill="currentColor"
      />
      <path
        d="M20.5594 11.8999C21.1086 11.9583 21.5064 12.4509 21.4479 13.0001C21.1378 15.9142 19.4855 18.6493 16.75 20.2287C12.6212 22.6124 7.44688 21.5447 4.56792 17.9208L4.45934 18.3261C4.3164 18.8595 3.76806 19.1761 3.23459 19.0332C2.70113 18.8902 2.38454 18.3419 2.52749 17.8084L3.25954 15.0764C3.40248 14.5429 3.95082 14.2263 4.48428 14.3693L7.21633 15.1013C7.7498 15.2443 8.06638 15.7926 7.92344 16.3261C7.7805 16.8595 7.23216 17.1761 6.6987 17.0332L6.35434 16.9409C8.66387 19.5864 12.5939 20.3188 15.75 18.4966C17.9084 17.2504 19.2137 15.0942 19.4592 12.7884C19.5176 12.2392 20.0102 11.8414 20.5594 11.8999Z"
        fill="currentColor"
      />
      <path
        d="M13.0001 7.5014C13.0001 6.94911 12.5523 6.5014 12.0001 6.5014C11.4478 6.5014 11.0001 6.94911 11.0001 7.5014V12.0014C11.0001 12.3527 11.1844 12.6782 11.4856 12.8589L13.9856 14.3589C14.4591 14.643 15.0734 14.4895 15.3575 14.0159C15.6417 13.5423 15.4881 12.9281 15.0145 12.6439L13.0001 11.4352V7.5014Z"
        fill="currentColor"
      />
    </svg>,
  ],
]);

export const hardcodedIconNameMap = new Map<string, string>([
  ['Billed', 'bank-note01'],
  ['Invoiced', 'bank-note01'],
  ['Locked', 'lock01'],
  ['Billed and Locked', 'lock01'],
  ['Rejected', 'alert-triangle'],
  ['Approved', 'check-circle'],
  ['Verified', 'check-verified02'],
]);

export const defaultIcon = 'circle-line';

export const defaultColor = '343a3f';

export const defaultBackgroundColor = '343a3f14';

const colorBackgroundMapSource: Record<string, string | undefined> = {
  defaultColor: defaultBackgroundColor,
  c97c08: 'dd9a171f',
  df0038: 'fe818c1f',
  '257c6e': '28bc981f',
  '0072c3': '33b1ff1f',
  d12771: 'ff7eb61f',
  ec6145: 'ff84661f',
  '6929c4': 'be95ff1f',
};

/**
 * Maps hex foreground colors to their corresponding background colors. If no matching background
 * color is found, it will be generated and cached for future lookups.
 *
 * Keys are case insensitive and will ignore any leading hash symbol or alpha channel.
 * @example
 * // These are all equally valid and point to the same value:
 * colorBackgroundMap['ec6145']
 * colorBackgroundMap['#ec6145']
 * colorBackgroundMap['ec61451f']
 * colorBackgroundMap['#EC6145']
 */
export const colorBackgroundMap: Readonly<Record<string, string>> = new Proxy(
  colorBackgroundMapSource as Record<string, string>,
  {
    get: (target, key) => {
      const foreground = stripHexPrefix(key.toString()).slice(0, 6).toLowerCase();
      if (!foreground) return defaultBackgroundColor;

      let background = target[foreground];

      if (!background) {
        background = stripHexPrefix(
          hexAdjustHsluv(foreground, ([h, s, l]) => [h, s, l + 20]) + '1f'
        );
        target[foreground] = background;
      }

      return prefixHexColor(background);
    },
  }
);
